import { useId, useRef, useState } from 'react'
import { Alert } from './Alert'
import { Button } from './Button'
import { Dialog } from './Dialog'
import { ErrorSummary, type ErrorSummaryItem } from './ErrorSummary'
import { Input } from './Input'
import { Select } from './Select'

/**
 * "Need some help?" trigger + dialog. Mirrors the legacy NavigationHeader
 * help flow but built on the shared Radix Dialog. Shared between the
 * spare-parts and screens apps — each passes its own mailer via `onSubmit`
 * (`/v1/sp/email/help` vs `/v1/screen/email/help`; the body shape is
 * identical).
 *
 * The state dropdown mirrors the legacy availability list — only VIC, QLD,
 * and NSW are serviced; the other states/territories are shown disabled
 * so users can see we operate elsewhere but can't actually file the
 * request from those regions.
 *
 * Validation follows the same WCAG 2.2 pattern as the checkout pages —
 * per-field inline errors PLUS an error summary at the top (role="alert",
 * focus moved on submit failure) with jump links that focus each field.
 */

export type HelpRequest = {
  name: string
  email: string
  phone: string
  state: string
}

export type HelpDialogProps = {
  /** App-specific mailer the dialog posts to on a valid submit. */
  onSubmit: (body: HelpRequest) => Promise<unknown>
}

type AvailableState = 'VIC' | 'QLD' | 'NSW'

type Status = 'idle' | 'submitting' | 'success' | 'error'

type FieldKey = 'name' | 'email' | 'phone' | 'state'

const SERVICED: ReadonlyArray<AvailableState> = ['VIC', 'QLD', 'NSW']
const UNSERVICED: ReadonlyArray<string> = ['SA', 'TAS', 'WA', 'NT']

const FIELD_LABELS: Record<FieldKey, string> = {
  name: 'Name',
  email: 'Email',
  phone: 'Phone number',
  state: 'State',
}

// Same email format used by the checkouts — pragmatic, not RFC-perfect.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function HelpDialog({ onSubmit }: HelpDialogProps) {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState<Status>('idle')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [state, setState] = useState<string>('')
  // Per-field validation errors. Cleared per-key as the user edits.
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({})
  // Network / server failures that don't belong to a single field.
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Focused on submit-failure so screen readers announce the full list.
  const errorSummaryRef = useRef<HTMLDivElement>(null)

  // Unique per-dialog-instance ids so multiple dialogs can't collide.
  const reactId = useId()
  const fieldId = (k: FieldKey) => `${reactId}-${k}`

  const reset = () => {
    setStatus('idle')
    setName('')
    setEmail('')
    setPhone('')
    setState('')
    setErrors({})
    setSubmitError(null)
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) reset()
  }

  const clearError = (key: FieldKey) => {
    setErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  // Collect every validation failure in one pass so the summary lists them
  // all together rather than the user fixing one only to hit the next.
  const validate = (): Partial<Record<FieldKey, string>> => {
    const e: Partial<Record<FieldKey, string>> = {}
    if (!name.trim()) e.name = 'Enter your name'
    if (!email.trim()) e.email = 'Enter your email address'
    else if (!EMAIL_RE.test(email)) e.email = 'Enter a valid email address'
    if (!phone.trim()) e.phone = 'Enter your phone number'
    if (!state) e.state = 'Select a state'
    return e
  }

  // Inline email check on blur so a typo (`jane@example,com`) surfaces
  // before the user reaches Submit.
  const handleEmailBlur = () => {
    if (!email.trim()) {
      setErrors((prev) => ({ ...prev, email: 'Enter your email address' }))
    } else if (!EMAIL_RE.test(email)) {
      setErrors((prev) => ({ ...prev, email: 'Enter a valid email address' }))
    }
  }

  const focusErrorSummary = () => {
    // rAF so the summary has mounted before we move focus to it.
    window.requestAnimationFrame(() => errorSummaryRef.current?.focus())
  }

  const submit = async () => {
    setSubmitError(null)
    const validationErrors = validate()
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) {
      focusErrorSummary()
      return
    }
    setStatus('submitting')
    try {
      await onSubmit({ name, email, phone, state })
      setStatus('success')
    } catch {
      // Legacy copy preserved verbatim.
      setSubmitError('Unexpected error occured.')
      setStatus('error')
    }
  }

  const errorKeys = Object.keys(errors) as FieldKey[]
  const errorItems: ErrorSummaryItem[] = errorKeys.map((k) => ({
    key: k,
    label: FIELD_LABELS[k],
    message: errors[k]!,
    targetId: fieldId(k),
  }))

  return (
    <>
      <button
        type="button"
        className="al-help-trigger"
        onClick={() => setOpen(true)}
        aria-label="Open help form"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <span>Help</span>
      </button>

      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
        title={status === 'success' ? 'Request submitted' : 'Need some help?'}
      >
        {status === 'success' ? (
          <>
            <p>
              Thank you for submitting your request. A sales representative will be in touch
              shortly.
            </p>
            <div className="al-dialog__footer">
              <Button variant="primary-outline" fill onClick={() => handleOpenChange(false)}>
                Continue
              </Button>
            </div>
          </>
        ) : (
          <>
            <p>Submit your details and a member of our team will be in touch.</p>

            <ErrorSummary ref={errorSummaryRef} items={errorItems} />

            <Input
              id={fieldId('name')}
              label="Name"
              required
              error={errors.name}
              maxLength={70}
              autoComplete="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                clearError('name')
              }}
              className="input-text"
            />
            <Input
              id={fieldId('email')}
              label="Email"
              type="email"
              required
              error={errors.email}
              maxLength={241}
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                clearError('email')
              }}
              onBlur={handleEmailBlur}
              className="input-text"
            />
            <Input
              id={fieldId('phone')}
              label="Phone number"
              type="tel"
              required
              error={errors.phone}
              maxLength={30}
              autoComplete="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value)
                clearError('phone')
              }}
              className="input-text"
            />
            <Select
              id={fieldId('state')}
              label="State"
              required
              error={errors.state}
              autoComplete="address-level1"
              value={state}
              onChange={(e) => {
                setState(e.target.value)
                clearError('state')
              }}
            >
              <option value="">Select a state</option>
              {SERVICED.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              {UNSERVICED.map((s) => (
                <option key={s} value={s} disabled>
                  {s} - Service Unavailable
                </option>
              ))}
            </Select>
            {submitError ? <Alert variant="error">{submitError}</Alert> : null}
            <p className="al-help__call">
              Alternatively please call us on <strong>1800 ALWINDOWS</strong> for assistance.
            </p>
            <div className="al-dialog__footer">
              <Button
                variant="primary-outline"
                fill
                onClick={submit}
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Submitting…' : 'Submit'}
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </>
  )
}
